/*
Page-scoped entry point — GSAP is only loaded/executed on this template,
kept out of the site-wide theme.js bundle.
*/
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Swiper from 'swiper';
import { A11y, EffectFade, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import { initColorTransitionGroup } from './app/transitions/color';

gsap.registerPlugin(ScrollTrigger, SplitText);

const initLollipopReveals = (swiper) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (swiper.el.matches('.fading-sections')) {
        const initialLollipops = swiper.slides[swiper.activeIndex].querySelectorAll('.lollipop-item');
        gsap.set(initialLollipops, { autoAlpha: 0 });
    }

    let hasEntered = false;
    const reveal = () => {
        const lollipops = swiper.slides[swiper.activeIndex].querySelectorAll('.lollipop-item');

        gsap.fromTo(lollipops, {
            autoAlpha: 0,
            y: (_, lollipop) => (lollipop.classList.contains('lollipop-blue') ? 96 : 72),
        }, {
            autoAlpha: 1,
            duration: 0.65,
            ease: 'power3.out',
            overwrite: true,
            stagger: 0.14,
            y: 0,
        });
    };

    ScrollTrigger.create({
        trigger: swiper.el,
        start: 'top 80%',
        once: true,
        onEnter: () => {
            hasEntered = true;
            reveal();
        },
    });
    swiper.on('slideChangeTransitionStart', () => {
        if (hasEntered) reveal();
    });
};

const stackingSections = document.querySelector('.fifteen-stacking-sections');

if (stackingSections) {
    const slider = stackingSections.querySelector('.stacking-sections-body');

    if (slider) {
        const stage = stackingSections.querySelector('.stacking-stage');
        let observedFigure;
        const figureObserver = new ResizeObserver(() => syncArrowPosition(slider.swiper));
        const syncArrowPosition = (swiper) => {
            const figure = swiper?.slides[swiper.activeIndex]?.querySelector('.block-lollipop');

            if (!figure) return;

            if (figure !== observedFigure) {
                figureObserver.disconnect();
                figureObserver.observe(figure);
                observedFigure = figure;
            }
            const stageTop = stage.getBoundingClientRect().top;
            const figureBounds = figure.getBoundingClientRect();
            stage.style.setProperty('--stacking-arrow-top', `${figureBounds.top - stageTop + figureBounds.height / 2}px`);
        };

        stackingSections.classList.add('is-stacking-active');
        new Swiper(slider, {
            modules: [A11y, EffectFade, Navigation],
            effect: 'fade',
            fadeEffect: { crossFade: true },
            speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 600,
            navigation: {
                prevEl: stackingSections.querySelector('.stacking-arrow.arrow-prev'),
                nextEl: stackingSections.querySelector('.stacking-arrow.arrow-next'),
            },
            a11y: {
                containerMessage: 'Investment strategies over time',
                slideLabelMessage: '{{index}} of {{slidesLength}}',
                prevSlideMessage: 'Show previous investment',
                nextSlideMessage: 'Show next investment',
            },
            on: {
                init: (swiper) => {
                    syncArrowPosition(swiper);
                    initLollipopReveals(swiper);
                },
                slideChange: syncArrowPosition,
                resize: syncArrowPosition,
            },
        });
    }
}

const fadingSections = document.querySelector('#fifteen-fading-sections');

if (fadingSections) {
    const slider = fadingSections.querySelector('.fading-sections');

    if (slider) {
        fadingSections.classList.add('is-fading-active');
        new Swiper(slider, {
            modules: [A11y, EffectFade, Navigation],
            effect: 'fade',
            fadeEffect: { crossFade: true },
            speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 600,
            navigation: {
                prevEl: fadingSections.querySelector('.fading-arrow.arrow-prev'),
                nextEl: fadingSections.querySelector('.fading-arrow.arrow-next'),
            },
            a11y: {
                containerMessage: 'High-growth company comparisons',
                slideLabelMessage: '{{index}} of {{slidesLength}}',
                prevSlideMessage: 'Show previous chart',
                nextSlideMessage: 'Show next chart',
            },
            on: {
                init: initLollipopReveals,
            },
        });
    }
}

const colorGroups = new Map();
document.querySelectorAll('[data-transition="color"]').forEach((el) => {
    const group = colorGroups.get(el.parentElement) || [];
    group.push(el);
    colorGroups.set(el.parentElement, group);
});

colorGroups.forEach((elements) => initColorTransitionGroup(elements, { gsap, ScrollTrigger, SplitText }));

const timeline = document.querySelector('.fifteen-timeline');

if (timeline) {
    const dateButtons = [...timeline.querySelectorAll('.timeline-date')];
    const datesElement = timeline.querySelector('.timeline-dates');
    const contentElement = timeline.querySelector('.timeline-content');

    if (dateButtons.length && datesElement && contentElement) {

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dates = new Swiper(datesElement, {
        freeMode: true,
        slidesPerView: 'auto',
        watchSlidesProgress: true,
    });

    const setActiveDate = (activeIndex) => {
        dateButtons.forEach((button, index) => {
            const isActive = index === activeIndex;
            if (isActive) button.setAttribute('aria-current', 'true');
            if (!isActive) button.removeAttribute('aria-current');
            button.tabIndex = isActive ? 0 : -1;
        });

        dates.slideTo(activeIndex, reducedMotion ? 0 : 300);
    };

    const content = new Swiper(contentElement, {
        modules: [A11y, Navigation],
        roundLengths: true,
        simulateTouch: false,
        slidesPerView: 1,
        spaceBetween: 40,
        speed: reducedMotion ? 0 : 600,
        navigation: {
            prevEl: timeline.querySelector('.timeline-arrow.arrow-prev'),
            nextEl: timeline.querySelector('.timeline-arrow.arrow-next'),
        },
        a11y: {
            containerMessage: 'Middle market research timeline',
            slideLabelMessage: '{{index}} of {{slidesLength}}',
        },
        on: {
            slideChange(swiper) {
                setActiveDate(swiper.activeIndex);
            },
        },
    });

    const selectDate = (index, shouldFocus = false) => {
        content.slideTo(index);

        if (shouldFocus) dateButtons[index].focus();
    };

    dateButtons.forEach((button, index) => {
        button.tabIndex = index === 0 ? 0 : -1;
        button.addEventListener('click', () => selectDate(index));
        button.addEventListener('keydown', (event) => {
            const keyOffsets = { ArrowLeft: -1, ArrowRight: 1 };
            let nextIndex = index + (keyOffsets[event.key] || 0);

            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = dateButtons.length - 1;
            if (!keyOffsets[event.key] && event.key !== 'Home' && event.key !== 'End') return;

            event.preventDefault();
            nextIndex = Math.max(0, Math.min(nextIndex, dateButtons.length - 1));
            selectDate(nextIndex, true);
        });
    });
    }
}

// Scale and subtly slide in each pair of lollipops when its chart enters the viewport.
const horizontalLollipopCharts = document.querySelectorAll('.horizontal-lollipop-chart');

if (horizontalLollipopCharts.length) {
    gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
        horizontalLollipopCharts.forEach((chart) => {
            const rows = [...chart.querySelectorAll('.horizontal-lollipop-chart__row')];
            const markers = rows.flatMap((row) => [...row.querySelectorAll('.chart-value')]);

            if (!markers.length) return;

            gsap.from(markers, {
                autoAlpha: 0,
                duration: 1.25,
                ease: 'power2.out',
                left: '0%',
                stagger: {
                    amount: 0.7,
                    from: 'start',
                },
                scrollTrigger: {
                    trigger: chart,
                    start: 'top 82%',
                    once: true,
                },
            });
        });
    });
}

const blockCallouts = document.querySelectorAll('.block-callout');

if (blockCallouts.length) {
    gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
        blockCallouts.forEach((callout) => {
            gsap.from(callout, {
                autoAlpha: 0,
                duration: 0.8,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: callout,
                    start: 'top 85%',
                    once: true,
                },
            });
        });
    });
}

// Animate each bar chart's bars up from 0 the first time it scrolls into view.
const barCharts = document.querySelectorAll('.bar-chart');

if (barCharts.length) {
    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference)', () => {
        barCharts.forEach((chart) => {
            const fills = chart.querySelectorAll('.bar-chart-fill');

            if (!fills.length) return;

            gsap.set(fills, { scaleY: 0 });
            gsap.to(fills, {
                scaleY: 1,
                duration: 0.8,
                ease: 'power2.out',
                stagger: 0.03,
                scrollTrigger: {
                    trigger: chart,
                    start: 'top 85%',
                    once: true,
                },
            });
        });
    });
}
